import { Calendar, Clock, DollarSign, Package } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Card, PageHeader } from "../../components/ui/patrones";
import { Aviso, Boton, Campo, Etiqueta, Tabla } from "../../components/ui/primitivos";
import {
  asignarOrden,
  borrarHoras,
  cambiarEstadoOrden,
  corregirHoras,
  eliminarOrden,
  fetchImpresoras,
  fetchOrden,
  fetchPartesHora,
  fetchUsuarios,
  registrarHoras,
} from "../../lib/consola-api";
import { useSession } from "../../lib/SessionContext";
import { useDatos } from "../../lib/useDatos";
import { SeccionFacturacionOt } from "../facturacion/SeccionFacturacionOt";
import { ESTADOS_IMPRESORA } from "../impresoras/estado";
import { SeccionArtes, SeccionPruebas } from "./adjuntos";
import { exigeMotivo, puedeForzar } from "./cambio-de-estado";
import { DialogoCambioDeEstado, type ModoDelDialogo } from "./DialogoCambioDeEstado";
import { DialogoEliminarOt } from "./DialogoEliminarOt";
import { eliminacionDe, esSoloLectura, puedeEliminar } from "./eliminar";
import { etiquetaDeEstado, TONO } from "./estados";
import { avisoDeUrgencia, fecha, fechaHora, horas, importe } from "./orden";

/**
 * El detalle de una OT, porteado de `JobDetail.tsx` de `imprenta` (ADR 0064).
 *
 * **Es una pantalla y no un modal**, que es la diferencia que motivó el port:
 * allá `/producciones/:id` es una ruta con una grilla de tres columnas —el
 * trabajo a la izquierda, la asignación y el arte a la derecha— y acá todo eso
 * estaba apilado en una columna de 512 px con scroll propio. Ver `ADR 0064`
 * para el criterio general.
 *
 * El reparto de columnas es el de allá y no es decorativo: a la izquierda va lo
 * que se LEE de la OT, a la derecha lo que se le HACE. Quien entra a asignar
 * una impresora no tiene que recorrer las fechas para llegar.
 */
export function OrdenPagina() {
  const { id = "" } = useParams<{ id: string }>();
  const navegar = useNavigate();
  const { can, canCodigo } = useSession();
  const veFacturacion =
    can("facturacion", "read") ||
    can("facturacion", "revertir") ||
    can("produccion", "asignar") ||
    can("produccion", "flexible_transition");

  const detalle = useDatos(() => fetchOrden(id), id);
  const usuarios = useDatos(() =>
    can("usuarios", "read") ? fetchUsuarios() : Promise.resolve([]),
  );
  const impresoras = useDatos(() =>
    can("impresoras", "read") ? fetchImpresoras() : Promise.resolve([]),
  );
  const partes = useDatos(
    () => (can("produccion", "horas") ? fetchPartesHora(id) : Promise.resolve(null)),
    id,
  );

  const [error, setError] = useState<string | null>(null);
  const [trabajando, setTrabajando] = useState(false);
  const [dialogo, setDialogo] = useState<ModoDelDialogo | null>(null);
  const [eliminando, setEliminando] = useState(false);

  async function accion(fn: () => Promise<unknown>) {
    setTrabajando(true);
    setError(null);
    try {
      await fn();
      await detalle.recargar();
    } catch (err) {
      setError(err instanceof Error ? err.message : "La acción falló");
    } finally {
      setTrabajando(false);
    }
  }

  // Una OT que no existe y una que existe pero no se puede ver dan el MISMO
  // 404 desde el worker (ADR 0019): distinguirlas acá reintroduciría por la
  // pantalla la enumeración que el endpoint cierra.
  if (detalle.error) {
    return (
      <div className="space-y-6">
        <PageHeader titulo="Orden de trabajo" alVolver={() => navegar("/produccion")} />
        <Aviso>{detalle.error}</Aviso>
      </div>
    );
  }

  const orden = detalle.datos?.orden;
  if (!orden) {
    return <p className="p-8 text-center text-neutral-500">Cargando orden de trabajo…</p>;
  }

  const rel = detalle.datos?.relacionados;
  const urgencia = avisoDeUrgencia(orden);
  // Una ELIMINADA se lee entera y no se le hace nada: toda escritura es 409
  // (ADR 0100 B8), así que no se ofrece ninguna.
  const soloLectura = esSoloLectura(String(orden.estado));
  const eliminacion = soloLectura ? eliminacionDe(detalle.datos?.historial ?? []) : null;

  return (
    <div className="space-y-6">
      <PageHeader
        titulo={orden.numero}
        subtitulo={
          <span className="font-medium text-neutral-700">
            Cliente: {rel?.cliente ?? "Sin cliente asignado"}
          </span>
        }
        alVolver={() => navegar("/produccion")}
      >
        <div className="flex flex-wrap items-center gap-2">
          <Etiqueta tono={String(orden.prioridad) === "BAJA" ? "neutral" : "aviso"}>
            {String(orden.prioridad ?? "NORMAL")}
          </Etiqueta>
          {urgencia ? <Etiqueta tono="error">{urgencia}</Etiqueta> : null}
          <Etiqueta>{String(orden.producto)}</Etiqueta>
          <Etiqueta tono={TONO[String(orden.estado)] ?? "neutral"}>
            {etiquetaDeEstado(String(orden.estado))}
          </Etiqueta>
          <span className="ml-auto text-neutral-500 text-xs">
            Creado por {rel?.creadoPor ?? "—"} · {fecha(orden.creadoEn)}
          </span>
        </div>
      </PageHeader>

      {error ? <Aviso>{error}</Aviso> : null}

      {eliminando ? (
        <DialogoEliminarOt
          numero={orden.numero}
          onCerrar={() => setEliminando(false)}
          alConfirmar={async (motivo) => {
            await eliminarOrden(id, motivo || undefined);
            navegar("/produccion");
          }}
        />
      ) : null}

      {dialogo ? (
        <DialogoCambioDeEstado
          numero={orden.numero}
          estadoActual={String(orden.estado)}
          modo={dialogo}
          onCerrar={() => setDialogo(null)}
          alConfirmar={async (destino, motivo) => {
            await cambiarEstadoOrden(id, destino, { motivo, forzar: dialogo.tipo === "forzar" });
            await detalle.recargar();
          }}
        />
      ) : null}

      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {/* Izquierda: lo que se LEE de la OT. */}
        <div className="space-y-6 md:col-span-2">
          <Seccion titulo="Estado de producción">
            {/* Los botones salen de lo que el worker dice que ESTA persona
                puede hacer ahora, no de una copia de la máquina de estados en
                el cliente. */}
            {soloLectura ? (
              <Aviso tono="neutral">
                <p className="font-semibold">OT eliminada</p>
                {/* Sin fila en el historial no hay quién ni cuándo que mostrar. */}
                {eliminacion ? (
                  <p>
                    Eliminada por {eliminacion.quien ?? "—"} el {fechaHora(eliminacion.cuando)}.
                    {eliminacion.motivo ? ` Motivo: ${eliminacion.motivo}` : ""}
                  </p>
                ) : (
                  <p>Sin registro de quién la eliminó.</p>
                )}
              </Aviso>
            ) : (detalle.datos?.transicionesDisponibles ?? []).length === 0 ? (
              <p className="text-neutral-500 text-sm">
                No hay transiciones disponibles para vos en este estado.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {(detalle.datos?.transicionesDisponibles ?? []).map((destino) => (
                  <Boton
                    key={destino}
                    variante={
                      destino === "CANCELADA" || destino === "RECHAZADA" ? "peligro" : "primario"
                    }
                    disabled={trabajando}
                    onClick={() =>
                      exigeMotivo(String(orden.estado), destino, false)
                        ? setDialogo({ tipo: "ordinario", destino })
                        : accion(() => cambiarEstadoOrden(id, destino))
                    }
                  >
                    {destino}
                  </Boton>
                ))}
              </div>
            )}
            {/* El salto forzado, con cualquiera de los dos permisos de salto
                (ADR 0098 B14). El worker revalida cada uno. */}
            {!soloLectura && puedeForzar(canCodigo) ? (
              <div className="border-neutral-200 border-t pt-3">
                <Boton
                  variante="secundario"
                  disabled={trabajando}
                  onClick={() => setDialogo({ tipo: "forzar" })}
                >
                  Forzar estado…
                </Boton>
              </div>
            ) : null}
            {/* Solo desde BORRADOR y con `produccion.delete` (ADR 0100 B3). */}
            {puedeEliminar(String(orden.estado), can) ? (
              <div className="border-neutral-200 border-t pt-3">
                <Boton variante="peligro" disabled={trabajando} onClick={() => setEliminando(true)}>
                  Eliminar OT
                </Boton>
              </div>
            ) : null}
          </Seccion>

          <Seccion titulo="Información general">
            <div className="grid grid-cols-2 gap-4">
              <Dato etiqueta="Producto">{String(orden.producto)}</Dato>
              <Dato etiqueta="Cantidad">
                <span className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-neutral-400" />
                  {String(orden.cantidad)} unidades
                </span>
              </Dato>
              <Dato etiqueta="Tipo de trabajo">{rel?.tipoTrabajo ?? "—"}</Dato>
              <Dato etiqueta="Material del cliente">
                <span className="flex items-center gap-2">
                  <Etiqueta>{String(orden.estadoStock ?? "Sin estado")}</Etiqueta>
                  {orden.fechaIngresoMercaderia ? (
                    <span className="text-neutral-500 text-xs">
                      ({fecha(orden.fechaIngresoMercaderia)})
                    </span>
                  ) : null}
                </span>
              </Dato>
              {orden.presupuestoId ? (
                <div className="col-span-2">
                  <span className="text-neutral-500 text-sm">Origen</span>
                  <div className="mt-1 flex items-center gap-2">
                    <Etiqueta>Consulta {rel?.presupuestoNumero ?? ""}</Etiqueta>
                    <button
                      type="button"
                      onClick={() => navegar(`/presupuestos?abrir=${String(orden.presupuestoId)}`)}
                      className="text-brand-600 text-xs hover:underline"
                    >
                      Ver consulta
                    </button>
                  </div>
                </div>
              ) : null}
            </div>

            {orden.observaciones ? (
              <div className="border-neutral-200 border-t pt-4">
                <span className="text-neutral-500 text-sm">Observaciones</span>
                <p className="mt-1 text-sm">{String(orden.observaciones)}</p>
              </div>
            ) : null}
          </Seccion>

          <Seccion titulo="Fechas" icono={<Calendar className="h-4 w-4" />}>
            <Fila etiqueta="Fecha pactada de entrega" valor={fecha(orden.fechaPactadaEntrega)} />
            <Fila etiqueta="Inicio de impresión" valor={fechaHora(orden.fechaInicioImpresion)} />
            <Fila etiqueta="Fin de impresión" valor={fechaHora(orden.fechaFinImpresion)} />
            <Fila etiqueta="Fecha entregada" valor={fechaHora(orden.fechaEntregada)} />
            <Fila etiqueta="Creado" valor={fechaHora(orden.creadoEn)} />
          </Seccion>

          <Seccion titulo="Horas de trabajo" icono={<Clock className="h-4 w-4" />}>
            <Fila etiqueta="Estimación mínima" valor={horas(orden.hsEstimMin)} />
            <Fila etiqueta="Estimación máxima" valor={horas(orden.hsEstimMax)} />
            <div className="flex items-center justify-between border-neutral-200 border-t pt-3">
              <span className="font-medium text-sm">Horas reales</span>
              <span className="font-bold text-brand-600 text-sm">{horas(orden.hsReales ?? 0)}</span>
            </div>

            {!soloLectura && can("produccion", "horas") ? (
              <PartesDeHora
                produccionId={id}
                partes={partes}
                trabajando={trabajando}
                accion={accion}
              />
            ) : null}
          </Seccion>

          {/* No está en `imprenta`: allá la vida de la OT solo se reconstruye
              desde la auditoría. Acá el worker ya devuelve `historial` con cada
              transición, y esconderlo sería tirar un dato que ya viaja. */}
          <Seccion titulo="Historial">
            <Tabla cabeceras={["Cuándo", "Cambio", "Motivo"]}>
              {(detalle.datos?.historial ?? []).map((h) => (
                <tr key={h.id}>
                  <td className="px-4 py-1.5 text-neutral-500 text-xs">{fechaHora(h.ocurrioEn)}</td>
                  <td className="px-4 py-1.5 text-sm">
                    {h.estadoAnterior} → {h.estadoNuevo}
                    {/* El salto forzado se ve como salto sin ir a la auditoría (ADR 0098 B9). */}
                    {h.forzada ? (
                      <span className="ml-2">
                        <Etiqueta tono="aviso">Forzado</Etiqueta>
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-1.5 text-neutral-600 text-sm">{h.motivo ?? "—"}</td>
                </tr>
              ))}
            </Tabla>
          </Seccion>
        </div>

        {/* Derecha: lo que se le HACE a la OT. */}
        <div className="space-y-6">
          {!soloLectura && can("produccion", "asignar") ? (
            <Seccion titulo="Asignación">
              <label className="block space-y-1">
                <span className="text-neutral-600 text-sm">Impresora</span>
                <select
                  aria-label="Impresora"
                  value={String(orden.impresoraId ?? "")}
                  onChange={(e) =>
                    accion(() => asignarOrden(id, { impresoraId: e.target.value || null }))
                  }
                  className="w-full rounded-md border border-neutral-300 bg-white px-2 py-1.5 text-neutral-900 text-sm"
                >
                  <option value="">— sin asignar —</option>
                  {(impresoras.datos ?? []).map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.nombre} · {ESTADOS_IMPRESORA[i.estado].etiqueta}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block space-y-1">
                <span className="text-neutral-600 text-sm">Responsable</span>
                <select
                  aria-label="Responsable"
                  value={String(orden.responsableId ?? "")}
                  onChange={(e) =>
                    accion(() => asignarOrden(id, { responsableId: e.target.value || null }))
                  }
                  className="w-full rounded-md border border-neutral-300 bg-white px-2 py-1.5 text-neutral-900 text-sm"
                >
                  <option value="">— sin asignar —</option>
                  {(usuarios.datos ?? []).map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.nombre}
                    </option>
                  ))}
                </select>
              </label>
            </Seccion>
          ) : null}

          {/* El arte va arriba de la columna: sin al menos uno la OT no se
              planifica, así que es lo que se viene a resolver cuando está en
              ARTE_PENDIENTE. */}
          <Seccion titulo={`Arte (${detalle.datos?.artes ?? 0})`}>
            <SeccionArtes
              entidadTipo="PRODUCCION"
              entidadId={id}
              soloLectura={soloLectura}
              // Recarga el detalle: con el primer arte aparece la transición a
              // PLANIFICADA, y los botones salen de `transicionesDisponibles`.
              alCambiar={() => detalle.recargar()}
            />
          </Seccion>

          {orden.observacionesLogo ? (
            <Seccion titulo="Observaciones del logo">
              <p className="text-sm">{String(orden.observacionesLogo)}</p>
            </Seccion>
          ) : null}

          <Seccion titulo="Pruebas de impresión">
            <SeccionPruebas
              produccionId={id}
              estado={String(orden.estado)}
              soloLectura={soloLectura}
              alCambiar={() => detalle.recargar()}
            />
          </Seccion>

          {/* `produccion.precios` está deliberadamente ausente del Operario: el
              worker le manda `total: null` y acá no se dibuja la tarjeta. */}
          {can("produccion", "precios") ? (
            <Seccion titulo="Información comercial" icono={<DollarSign className="h-4 w-4" />}>
              <Fila etiqueta="Precio unitario" valor={importe(orden.precioUnitario)} />
              <Fila etiqueta="Total" valor={importe(orden.total)} />
              {orden.facturaRef ? (
                <Fila etiqueta="Factura" valor={String(orden.facturaRef)} />
              ) : null}
              {orden.metodoPago ? (
                <Fila etiqueta="Método de pago" valor={String(orden.metodoPago)} />
              ) : null}
              {orden.fechaPago ? (
                <Fila etiqueta="Fecha de pago" valor={fecha(orden.fechaPago)} />
              ) : null}
            </Seccion>
          ) : null}

          {/* Los permisos que acepta la alerta por OT (ADR 0099 B3). */}
          {veFacturacion ? (
            <Seccion titulo="Facturación">
              <SeccionFacturacionOt otId={id} />
            </Seccion>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** Una tarjeta con título, como las `Card` de `JobDetail`. */
function Seccion({
  titulo,
  icono,
  children,
}: {
  titulo: string;
  icono?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card className="space-y-3 p-4">
      <h2 className="flex items-center gap-2 font-semibold text-neutral-900 text-sm">
        {icono}
        {titulo}
      </h2>
      {children}
    </Card>
  );
}

/** Etiqueta arriba, valor abajo — el par de «Información general». */
function Dato({ etiqueta, children }: { etiqueta: string; children: ReactNode }) {
  return (
    <div>
      <span className="text-neutral-500 text-sm">{etiqueta}</span>
      <p className="font-medium text-sm">{children}</p>
    </div>
  );
}

/** Etiqueta a la izquierda, valor a la derecha — el par de «Fechas» y «Horas». */
function Fila({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-neutral-500 text-sm">{etiqueta}</span>
      <span className="font-medium text-sm">{valor}</span>
    </div>
  );
}

/**
 * Carga de horas y los partes ya cargados, con su corrección y su baja.
 *
 * Sin la lista, un parte mal cargado solo se ve en el acumulado —que no dice
 * cuál de todos está mal— y no habría forma de arreglarlo.
 */
function PartesDeHora({
  produccionId,
  partes,
  trabajando,
  accion,
}: {
  produccionId: string;
  partes: ReturnType<typeof useDatos<Awaited<ReturnType<typeof fetchPartesHora>> | null>>;
  trabajando: boolean;
  accion: (fn: () => Promise<unknown>) => Promise<void>;
}) {
  const [carga, setCarga] = useState({
    fecha: new Date().toISOString().slice(0, 10),
    horas: "",
  });
  const [editando, setEditando] = useState<string | null>(null);

  return (
    <div className="space-y-3 border-neutral-200 border-t pt-3">
      <div className="flex items-end gap-2">
        <Campo
          etiqueta="Fecha"
          type="date"
          value={carga.fecha}
          onChange={(e) => setCarga((h) => ({ ...h, fecha: e.target.value }))}
        />
        <Campo
          etiqueta="Horas"
          type="number"
          value={carga.horas}
          onChange={(e) => setCarga((h) => ({ ...h, horas: e.target.value }))}
        />
        <Boton
          disabled={trabajando || carga.horas === ""}
          onClick={() =>
            accion(async () => {
              await registrarHoras(produccionId, {
                fecha: carga.fecha,
                horas: Number(carga.horas),
              });
              setCarga((h) => ({ ...h, horas: "" }));
              await partes.recargar();
            })
          }
        >
          Cargar
        </Boton>
      </div>

      <Tabla cabeceras={["Fecha", "Horas", "Quién", ""]}>
        {(partes.datos?.partes ?? []).map((parte) => (
          <tr key={parte.id}>
            <td className="px-4 py-1.5 text-sm">{fecha(parte.fecha)}</td>
            <td className="px-4 py-1.5 text-sm">
              {editando === parte.id ? (
                <input
                  aria-label="Corregir horas"
                  type="number"
                  defaultValue={parte.horas}
                  onKeyDown={(e) => {
                    if (e.key !== "Enter") return;
                    const valor = Number((e.target as HTMLInputElement).value);
                    accion(async () => {
                      await corregirHoras(produccionId, parte.id, { horas: valor });
                      setEditando(null);
                      await partes.recargar();
                    });
                  }}
                  className="w-20 rounded border border-neutral-300 bg-white px-1.5 py-0.5 text-sm"
                />
              ) : (
                horas(parte.horas)
              )}
            </td>
            <td className="px-4 py-1.5 text-neutral-600 text-sm">{parte.usuario ?? "—"}</td>
            <td className="px-4 py-1.5 text-right">
              <Boton
                variante="fantasma"
                disabled={trabajando}
                onClick={() => setEditando(editando === parte.id ? null : parte.id)}
              >
                {editando === parte.id ? "Cancelar" : "Corregir"}
              </Boton>
              <Boton
                variante="peligro"
                disabled={trabajando}
                onClick={() =>
                  accion(async () => {
                    await borrarHoras(produccionId, parte.id);
                    await partes.recargar();
                  })
                }
              >
                Borrar
              </Boton>
            </td>
          </tr>
        ))}
      </Tabla>
      <p className="text-neutral-500 text-xs">Total: {horas(partes.datos?.totalHoras ?? 0)}</p>
    </div>
  );
}
